import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import {
  getInventoryItemDetail,
  listInventoryItems,
  type InventoryItemListRow,
} from '@/lib/api/inventory.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { addTicketPart } from '@/lib/api/ticket-parts.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatCurrency } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function partCompatibleModelIds(item: InventoryItemListRow): string[] {
  if (item.compatibleModelIds?.length) return item.compatibleModelIds;
  return (item.compatibleModels ?? []).map((m) => m.modelId).filter(Boolean);
}

function isPartCompatibleWithDevice(
  item: InventoryItemListRow,
  deviceModelId: string | null | undefined,
): boolean {
  if (!deviceModelId) return true;
  const ids = partCompatibleModelIds(item);
  if (ids.length === 0) return false;
  return ids.includes(deviceModelId);
}

export function AddPartPanel({
  ticketId,
  repairId,
  trackSerialParts,
  deviceModelId,
  onSuccess,
  onOrderPart,
  onClose,
}: {
  ticketId: string;
  repairId: string;
  trackSerialParts?: boolean;
  deviceModelId?: string | null;
  onSuccess: () => void;
  onOrderPart?: () => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'inventory' | 'one_time'>('inventory');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [selectedItem, setSelectedItem] = useState<InventoryItemListRow | null>(null);
  const [selectedSupplierVariantId, setSelectedSupplierVariantId] = useState<string>('');
  const [quantity, setQuantity] = useState('1');
  const [unitCost, setUnitCost] = useState('');
  const [partName, setPartName] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [outOfStockConfirm, setOutOfStockConfirm] = useState<InventoryItemListRow | null>(null);
  const [compatWarnConfirm, setCompatWarnConfirm] = useState<InventoryItemListRow | null>(null);

  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const partSearchMode = storeQuery.data?.partSearchMode ?? 'all';
  const partCompatibilityWarn = storeQuery.data?.partCompatibilityWarn ?? true;

  const filterByCompatibility = partSearchMode === 'compatible_only' && Boolean(deviceModelId);
  const showingAllBecauseNoModel =
    partSearchMode === 'compatible_only' && !deviceModelId;

  const compatibleQuery = useQuery({
    queryKey: ['inventory-items-compatible', deviceModelId],
    queryFn: () =>
      listInventoryItems({
        category: 'repair_part',
        compatibleModelId: deviceModelId!,
        pageSize: 15,
        sellableOnly: true,
      }),
    enabled: tab === 'inventory' && Boolean(deviceModelId) && debouncedSearch.trim().length < 2,
  });

  const itemsQuery = useQuery({
    queryKey: [
      'inventory-items-parts',
      debouncedSearch,
      filterByCompatibility ? deviceModelId : 'all',
    ],
    queryFn: () =>
      listInventoryItems({
        search: debouncedSearch,
        category: 'repair_part',
        pageSize: 20,
        sellableOnly: true,
        ...(filterByCompatibility ? { compatibleModelId: deviceModelId! } : {}),
      }),
    enabled: tab === 'inventory' && debouncedSearch.trim().length >= 2,
  });

  const searchItems = useMemo(() => itemsQuery.data?.data ?? [], [itemsQuery.data]);
  const compatibleItems = useMemo(
    () => compatibleQuery.data?.data ?? [],
    [compatibleQuery.data],
  );

  const detailQuery = useQuery({
    queryKey: ['inventory-item-part-detail', selectedItem?.id],
    queryFn: () => getInventoryItemDetail(selectedItem!.id),
    enabled: Boolean(selectedItem?.id),
  });

  const supplierOptions = useMemo(() => {
    const rows = detailQuery.data?.suppliers ?? [];
    return rows.filter((s) => s.active !== false);
  }, [detailQuery.data]);

  useEffect(() => {
    if (!selectedItem) {
      setSelectedSupplierVariantId('');
      return;
    }
    const preferred = supplierOptions.find((s) => s.preferred);
    const first = preferred ?? supplierOptions[0];
    if (first) {
      setSelectedSupplierVariantId(String(first.id));
      if (!unitCost && first.costPrice != null) {
        setUnitCost(String(first.costPrice));
      }
    }
  }, [selectedItem, supplierOptions]);

  const addMutation = useMutation({
    mutationFn: (body: Parameters<typeof addTicketPart>[1]) => addTicketPart(ticketId, body),
    onSuccess: (result) => {
      if (result.warning === 'OUT_OF_STOCK') {
        toastSuccess('Part added — inventory will go negative');
      } else {
        toastSuccess('Part added to repair');
      }
      onSuccess();
      onClose();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function needsCompatibilityWarning(item: InventoryItemListRow): boolean {
    return (
      partCompatibilityWarn &&
      Boolean(deviceModelId) &&
      !isPartCompatibleWithDevice(item, deviceModelId)
    );
  }

  function proceedSelectItem(item: InventoryItemListRow) {
    const qty = Number(item.qtyOnHand ?? 0);
    if (qty <= 0) {
      setOutOfStockConfirm(item);
      return;
    }
    setSelectedItem(item);
    setUnitCost('');
  }

  function selectItem(item: InventoryItemListRow) {
    if (needsCompatibilityWarning(item)) {
      setCompatWarnConfirm(item);
      return;
    }
    proceedSelectItem(item);
  }

  function confirmCompatibility(addAnyway: boolean) {
    if (!compatWarnConfirm) return;
    const item = compatWarnConfirm;
    setCompatWarnConfirm(null);
    if (addAnyway) {
      proceedSelectItem(item);
    }
  }

  function confirmOutOfStock(addAnyway: boolean) {
    if (!outOfStockConfirm) return;
    if (addAnyway) {
      setSelectedItem(outOfStockConfirm);
      setUnitCost('');
    }
    setOutOfStockConfirm(null);
  }

  function submitInventory(event: FormEvent) {
    event.preventDefault();
    if (!selectedItem) return;
    addMutation.mutate({
      ticketRepairId: repairId,
      inventoryItemId: selectedItem.id,
      quantity: Number(quantity) || 1,
      unitCost: unitCost ? Number(unitCost) : undefined,
    });
  }

  function submitOneTime(event: FormEvent) {
    event.preventDefault();
    if (!partName.trim()) {
      toastError('Part name is required');
      return;
    }
    addMutation.mutate({
      ticketRepairId: repairId,
      isOneTime: true,
      partName: partName.trim(),
      supplierName: supplierName.trim() || undefined,
      quantity: Number(quantity) || 1,
      unitCost: Number(unitCost) || 0,
    });
  }

  function renderItemButton(item: InventoryItemListRow, badge?: string) {
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => selectItem(item)}
        className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-left hover:border-primary"
      >
        <div>
          <div className="text-sm font-semibold text-slate-900">{item.name}</div>
          <div className="text-xs text-slate-500">
            Qty {item.qtyAvailable ?? item.qtyOnHand} · {formatCurrency(item.retailPrice)}
            {item.sku ? ` · SKU ${item.sku}` : ''}
            {badge ? ` · ${badge}` : ''}
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-bold text-slate-950">Add Part to Repair</h4>
        <button type="button" className="rounded-lg p-1 text-slate-500 hover:bg-white" onClick={onClose}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-3 flex gap-2">
        {(['inventory', 'one_time'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => {
              setTab(option);
              setSelectedItem(null);
            }}
            className={[
              'rounded-lg px-3 py-1.5 text-xs font-semibold',
              tab === option ? 'bg-primary text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200',
            ].join(' ')}
          >
            {option === 'inventory' ? 'From Inventory' : 'One-Time Part'}
          </button>
        ))}
      </div>

      {tab === 'inventory' ? (
        selectedItem ? (
          <form className="space-y-3" onSubmit={submitInventory}>
            <p className="text-sm font-semibold text-slate-800">{selectedItem.name} ✓</p>
            {supplierOptions.length > 1 ? (
              <label className="block text-sm">
                Supplier SKU
                <select
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={selectedSupplierVariantId}
                  onChange={(e) => {
                    setSelectedSupplierVariantId(e.target.value);
                    const row = supplierOptions.find((s) => String(s.id) === e.target.value);
                    if (row?.costPrice != null) setUnitCost(String(row.costPrice));
                  }}
                >
                  {supplierOptions.map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>
                      {String(s.supplierName ?? 'Supplier')}
                      {s.supplierSku ? ` — ${String(s.supplierSku)}` : ''}
                      {s.preferred ? ' (preferred)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="block text-sm">
              Quantity
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                type="number"
                min="0.01"
                step="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              Unit Cost (optional — auto-filled from inventory)
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                type="number"
                min="0"
                step="0.01"
                placeholder="Auto"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
              />
            </label>
            {trackSerialParts && selectedItem ? (
              <p className="text-xs text-slate-500">
                Serial tracking enabled — assign serial after adding if needed.
              </p>
            ) : null}
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded-xl border px-4 py-2 text-sm font-semibold"
                onClick={() => setSelectedItem(null)}
              >
                Back
              </button>
              <button
                type="submit"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={addMutation.isPending}
              >
                Add Part →
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            {showingAllBecauseNoModel ? (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Device has no model — showing all parts
              </p>
            ) : null}
            {partSearchMode === 'compatible_only' && deviceModelId ? (
              <p className="text-xs text-slate-500">
                Showing only parts marked compatible with this device.
              </p>
            ) : null}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm"
                placeholder="Search by name, SKU, vendor SKU, or barcode (2+ chars)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {deviceModelId && debouncedSearch.trim().length < 2 && compatibleItems.length > 0 ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Compatible with this device
                  </p>
                  {compatibleItems.map((item) => renderItemButton(item, 'Compatible'))}
                </>
              ) : null}
              {debouncedSearch.trim().length >= 2
                ? searchItems.map((item) =>
                    renderItemButton(
                      item,
                      deviceModelId && isPartCompatibleWithDevice(item, deviceModelId)
                        ? 'Compatible'
                        : undefined,
                    ),
                  )
                : null}
              {debouncedSearch.trim().length >= 2 && searchItems.length === 0 && !itemsQuery.isLoading ? (
                <p className="text-sm text-slate-500">No matching parts.</p>
              ) : null}
            </div>
            {onOrderPart ? (
              <button type="button" className="text-xs font-semibold text-primary" onClick={onOrderPart}>
                Order part instead →
              </button>
            ) : null}
          </div>
        )
      ) : (
        <form className="space-y-3" onSubmit={submitOneTime}>
          <label className="block text-sm">
            Part name
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={partName}
              onChange={(e) => setPartName(e.target.value)}
              required
            />
          </label>
          <label className="block text-sm">
            Supplier
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Quantity
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              type="number"
              min="0.01"
              step="0.01"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Unit cost
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              type="number"
              min="0"
              step="0.01"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={addMutation.isPending}
          >
            Add Part →
          </button>
        </form>
      )}

      {compatWarnConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
            <h5 className="font-bold text-slate-950">Compatibility check</h5>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure this part works with this device?{' '}
              <span className="font-medium text-slate-800">{compatWarnConfirm.name}</span> is not
              marked compatible with the ticket device
              {partCompatibleModelIds(compatWarnConfirm).length === 0
                ? ' (no model tags).'
                : '.'}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border px-3 py-2 text-sm font-semibold"
                onClick={() => confirmCompatibility(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                onClick={() => confirmCompatibility(true)}
              >
                Add anyway
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {outOfStockConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
            <h5 className="font-bold text-slate-950">Out of stock</h5>
            <p className="mt-2 text-sm text-slate-600">
              {outOfStockConfirm.name} has no quantity on hand. Add anyway?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border px-3 py-2 text-sm font-semibold"
                onClick={() => confirmOutOfStock(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                onClick={() => confirmOutOfStock(true)}
              >
                Add anyway
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
